import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { RouterProvider, match, navigate, useRoute } from "./lib/router";
import { SessionProvider, useSession } from "./lib/session";
import { Shell, type Workspace } from "./components/Shell";
import { LogProvider } from "./components/QuickLog";
import { PipelineProvider } from "./components/OppSheet";
import { PersonDrawerHost } from "./components/PersonDrawer";
import { Loading, ToastProvider } from "./components/ui";
import Login, { SetPassword } from "./pages/Login";
import ImportSolutions from "./pages/clients/Import";
import Today from "./pages/leads/Today";
import People from "./pages/leads/People";
import QuickAdd from "./pages/leads/QuickAdd";
import PhoneContacts from "./pages/settings/PhoneContacts";
import Pipeline from "./pages/leads/Pipeline";
import Events from "./pages/leads/Events";
import Content from "./pages/leads/Content";
import { MemberProfile, RecruitTracker, TeamOverview, TeamRoster } from "./pages/team/Team";
import TeamTree from "./pages/team/TeamTree";
import Birthdays from "./pages/Birthdays";
import { MfaChallenge, MfaForceEnroll, SettingsSecurity } from "./components/Mfa";
import Clients from "./pages/clients/Clients";
import ClientProfile from "./pages/clients/ClientProfile";
import { MeetingsPage } from "./components/Appointments";
import CommandCenter from "./pages/CommandCenter";
import Scoreboard from "./pages/Scoreboard";
import WeeklyReview from "./pages/WeeklyReview";
import MeetingPublic from "./pages/MeetingPublic";
import GoogleCallback from "./pages/settings/GoogleCallback";
import Cleanup from "./pages/leads/Cleanup";
import Removed from "./pages/leads/Removed";
import ClientReviews from "./pages/clients/Reviews";
import ReportsHub, { Redirect } from "./pages/ReportsHub";
import YourProfile from "./pages/settings/YourProfile";
import { SettingsGoals } from "./components/Goals";
import HowItWorks from "./pages/settings/HowItWorks";
import { TasksPage } from "./components/Tasks";
import SettingsAutomations from "./pages/settings/Automations";
import SettingsPipelines from "./pages/settings/Pipelines";
import { NewsletterEditor, NewsletterList } from "./pages/Newsletter";
import { NoAccess, SettingsLists, SettingsMilestones, SettingsRouting, SettingsRules, SettingsUsers } from "./pages/settings/Settings";
import { ClientFileRoute } from "./pages/clients/ClientFile";

type Route = { pattern: string; ws: Workspace; module: string; render: (p: Record<string, string>) => React.ReactNode };
const ROUTES: Route[] = [
  { pattern: "/command", ws: "home", module: "", render: () => <CommandCenter /> },
  { pattern: "/tasks", ws: "home", module: "tasks", render: () => <TasksPage /> },
  { pattern: "/review", ws: "home", module: "", render: () => <WeeklyReview /> },
  { pattern: "/leads/funnel", ws: "home", module: "", render: () => <Redirect to="/reports?view=funnel" /> },
  { pattern: "/leads/cleanup", ws: "leads", module: "leads", render: () => <Cleanup /> },
  { pattern: "/leads/removed", ws: "leads", module: "people_admin", render: () => <Removed /> },
  { pattern: "/scoreboard", ws: "home", module: "tasks", render: () => <Scoreboard /> },
  { pattern: "/newsletter", ws: "home", module: "newsletter", render: () => <NewsletterList /> },
  { pattern: "/newsletter/:id", ws: "home", module: "newsletter", render: (p) => <NewsletterEditor id={p.id} /> },
  { pattern: "/leads", ws: "leads", module: "today", render: () => <Today /> },
  { pattern: "/leads/people", ws: "leads", module: "leads", render: () => <People /> },
  { pattern: "/add", ws: "leads", module: "leads", render: () => <QuickAdd /> },
  { pattern: "/leads/pipeline", ws: "leads", module: "pipelines", render: () => <Pipeline /> },
  { pattern: "/leads/meetings", ws: "leads", module: "activities", render: () => <MeetingsPage /> },
  { pattern: "/leads/events", ws: "leads", module: "events", render: () => <Events /> },
  { pattern: "/leads/dashboards", ws: "home", module: "", render: () => <Redirect to="/reports?view=scorecard" /> },
  { pattern: "/leads/scorecard", ws: "home", module: "", render: () => <Redirect to="/reports?view=scorecard" /> },
  { pattern: "/leads/reports", ws: "home", module: "", render: () => <Redirect to="/reports?view=results" /> },
  { pattern: "/leads/content", ws: "leads", module: "content", render: () => <Content /> },
  { pattern: "/team/dashboard", ws: "home", module: "", render: () => <Redirect to="/reports?view=team" /> },
  { pattern: "/team", ws: "team", module: "team", render: () => <TeamOverview /> },
  { pattern: "/team/roster", ws: "team", module: "team", render: () => <TeamRoster /> },
  { pattern: "/team/tracker", ws: "team", module: "team", render: () => <RecruitTracker /> },
  { pattern: "/team/tree", ws: "team", module: "team", render: () => <TeamTree /> },
  { pattern: "/team/members/:id", ws: "team", module: "team", render: (p) => <MemberProfile id={p.id} /> },
  { pattern: "/team/birthdays", ws: "home", module: "", render: () => <Redirect to="/birthdays?g=team" /> },
  { pattern: "/leads/birthdays", ws: "home", module: "", render: () => <Redirect to="/birthdays?g=leads" /> },
  { pattern: "/clients/birthdays", ws: "home", module: "", render: () => <Redirect to="/birthdays?g=clients" /> },
  { pattern: "/birthdays", ws: "home", module: "", render: () => <Birthdays group="all" /> },
  { pattern: "/reports", ws: "home", module: "", render: () => <ReportsHub /> },
  { pattern: "/clients/reviews", ws: "clients", module: "clients", render: () => <ClientReviews /> },
  { pattern: "/clients/import", ws: "clients", module: "clients", render: () => <ImportSolutions /> },
  { pattern: "/clients/:id/file", ws: "clients", module: "clients", render: (p) => <ClientFileRoute id={p.id} /> },
  { pattern: "/clients/:id", ws: "clients", module: "clients", render: (p) => <ClientProfile id={p.id} /> },
  { pattern: "/clients", ws: "clients", module: "clients", render: () => <Clients /> },
  { pattern: "/settings", ws: "settings", module: "settings", render: () => <SettingsRules /> },
  { pattern: "/settings/milestones", ws: "settings", module: "settings", render: () => <SettingsMilestones /> },
  { pattern: "/settings/lists", ws: "settings", module: "settings", render: () => <SettingsLists /> },
  { pattern: "/settings/pipelines", ws: "settings", module: "settings", render: () => <SettingsPipelines /> },
  { pattern: "/settings/routing", ws: "settings", module: "settings", render: () => <SettingsRouting /> },
  { pattern: "/settings/google-callback", ws: "settings", module: "settings", render: () => <GoogleCallback /> },
  { pattern: "/settings/automations", ws: "settings", module: "settings", render: () => <SettingsAutomations /> },
  { pattern: "/settings/phone-contacts", ws: "settings", module: "settings", render: () => <PhoneContacts /> },
  { pattern: "/settings/security", ws: "settings", module: "", render: () => <SettingsSecurity /> },
  { pattern: "/settings/profile", ws: "settings", module: "", render: () => <YourProfile /> },
  { pattern: "/settings/goals", ws: "settings", module: "settings", render: () => <SettingsGoals /> },
  { pattern: "/settings/how", ws: "settings", module: "", render: () => <HowItWorks /> },
  { pattern: "/settings/users", ws: "settings", module: "users", render: () => <SettingsUsers /> },
];

function App() {
  const { session, profile, ready, can, mfaState } = useSession();
  const { path } = useRoute();

  // Home: first workspace this person can use
  useEffect(() => {
    if (!ready || !profile || path !== "/") return;
    navigate("/command", { replace: true });
  }, [ready, profile, path, can]);

  if (path === "/set-password") return <SetPassword />;
  if (path.startsWith("/m/")) return <MeetingPublic token={path.slice(3)} />;
  if (!ready) return <Loading rows={3} />;
  if (!session) return <Login />;
  if (mfaState.needs) return <MfaChallenge />;
  if (mfaState.mustEnroll) return <MfaForceEnroll />;
  if (!profile) return <Login noProfile />;

  let hit: { r: Route; params: Record<string, string> } | null = null;
  for (const r of ROUTES) {
    const m = match(r.pattern, path);
    if (m) { hit = { r, params: m }; break; }
  }
  const ws: Workspace = hit?.r.ws ?? "leads";
  return (
    <LogProvider workspace={ws}>
      <PipelineProvider>
      <Shell workspace={ws}>
        {!hit ? <NoAccess /> : (!hit.r.module || can(hit.r.module)) ? hit.r.render(hit.params) : <NoAccess />}
      </Shell>
      <PersonDrawerHost workspace={ws === "home" ? "leads" : ws} />
      </PipelineProvider>
    </LogProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RouterProvider>
      <SessionProvider>
        <ToastProvider>
          <App />
        </ToastProvider>
      </SessionProvider>
    </RouterProvider>
  </StrictMode>,
);
